import DialogSelectTeamPrompt from "@/components/team/dialog-select-team-prompt";
import { PinnedPromptsContextList } from "@/components/team/pinned-prompts-context-list";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import PageSkeleton from "@/components/page-skeleton";
import { useToast, useToastError } from "@/components/ui/use-toast";
import { MAX_PINNED_PROMPTS } from "@/enums/pinned-prompts";
import { useApi } from "@/hooks/useApi";
import { usePinnedPromptsEditor } from "@/hooks/usePinnedPromptsEditor";
import { useTranslation } from "@/hooks/useTranslation";
import { getPromptId, getSupportedLang } from "@/lib/prompt-utils";
import {
  ApplyTeamPinnedPromptsDefaultResultDTO,
  PinnedContext,
  PromptDTO,
  TeamPinnedPromptsDefaultDTO,
  TeamUserDTO,
} from "@shaarp/common";
import { PlusIcon } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

const CONTEXTS: PinnedContext[] = ["chat", "company"];

export function TeamSettingPinnedPromptsDefault() {
  const api = useApi();
  const { toast } = useToast();
  const toastError = useToastError();
  const { t, i18n } = useTranslation();
  const lang = getSupportedLang(i18n.language);

  const [defaults, setDefaults] = useState<TeamPinnedPromptsDefaultDTO>({
    chat: [],
    company: [],
  });
  const [prompts, setPrompts] = useState<PromptDTO[]>([]);
  const [teamMemberCount, setTeamMemberCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(true);
  const [pickerContext, setPickerContext] = useState<PinnedContext | null>(
    null,
  );
  const [applyContext, setApplyContext] = useState<PinnedContext | null>(
    null,
  );
  // Kept after the dialog closes so its text does not vanish mid-animation.
  const [confirmContext, setConfirmContext] = useState<PinnedContext>("chat");
  const [isApplying, setIsApplying] = useState(false);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [defaultsRes, promptsRes, usersRes] = await Promise.all([
        api.get<TeamPinnedPromptsDefaultDTO>("/my/team/prompts-pinned-default"),
        api.get<PromptDTO[]>("/prompts"),
        api.get<TeamUserDTO[]>("/my/team/users"),
      ]);
      setDefaults(defaultsRes.data);
      setPrompts(promptsRes.data);
      setTeamMemberCount(usersRes.data.length);
    } catch {
      toastError();
    } finally {
      setIsLoading(false);
    }
  }, [api, toastError]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const promptMap = new Map(prompts.map((p) => [getPromptId(p), p]));

  const { handleAdd, handleRemove, handleMove } = usePinnedPromptsEditor({
    state: defaults,
    setState: setDefaults,
    refetch: fetchData,
    buildAddUrl: (context, promptId) =>
      `/my/team/prompts-pinned-default/${promptId}?context=${context}`,
    buildRemoveUrl: (context, promptId) =>
      `/my/team/prompts-pinned-default/${promptId}?context=${context}`,
    buildReorderUrl: () => "/my/team/prompts-pinned-default/reorder",
  });

  const handleApply = async (context: PinnedContext) => {
    setIsApplying(true);
    try {
      const { data } = await api.post<ApplyTeamPinnedPromptsDefaultResultDTO>(
        `/my/team/prompts-pinned-default/apply?context=${context}`,
      );
      toast({
        title: t("team.components.pinnedPromptsDefault.applySuccess.title"),
        description: t(
          "team.components.pinnedPromptsDefault.applySuccess.description",
          { count: data.affectedMemberCount },
        ),
      });
    } catch {
      toastError();
    } finally {
      setIsApplying(false);
      setApplyContext(null);
    }
  };

  const getContextLabel = (context: PinnedContext): string =>
    t(
      `team.components.pinnedPromptsDefault.context${
        context === "chat" ? "Chat" : "Company"
      }`,
    );

  const renderContextBlock = (context: PinnedContext) => {
    const ids = defaults[context];
    const max = MAX_PINNED_PROMPTS[context];
    const limitReached = ids.length >= max;
    const contextLabel = getContextLabel(context);

    return (
      <div className="space-y-3">
        <PinnedPromptsContextList
          contextLabel={contextLabel}
          limitLabel={t("team.components.pinnedPromptsDefault.limitLabel", {
            count: ids.length,
            max,
          })}
          ids={ids}
          promptMap={promptMap}
          lang={lang}
          emptyStateLabel={t(
            "team.components.pinnedPromptsDefault.emptyState",
          )}
          moveUpAria={t("team.components.pinnedPromptsDefault.moveUpAria")}
          moveDownAria={t(
            "team.components.pinnedPromptsDefault.moveDownAria",
          )}
          removeAria={t("team.components.pinnedPromptsDefault.removeAria")}
          onRemove={(id) => handleRemove(context, id)}
          onMove={(index, direction) => handleMove(context, index, direction)}
        />

        <div className="flex flex-col gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={limitReached}
            onClick={() => setPickerContext(context)}
          >
            <PlusIcon className="mr-2 h-4 w-4" />
            {t("team.components.pinnedPromptsDefault.addButton")}
          </Button>
          {limitReached && (
            <p className="text-xs text-muted-foreground">
              {t("team.components.pinnedPromptsDefault.limitReached", { max })}
            </p>
          )}
          {/* Disabled when empty: applying an empty default would wipe this
              context for every member (the API refuses it too). */}
          <Button
            variant="outline"
            size="sm"
            disabled={ids.length === 0}
            onClick={() => {
              setConfirmContext(context);
              setApplyContext(context);
            }}
          >
            {t("team.components.pinnedPromptsDefault.applyButton")}
          </Button>
        </div>
      </div>
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("team.components.pinnedPromptsDefault.title")}</CardTitle>
        <CardDescription>
          {t("team.components.pinnedPromptsDefault.description")}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <PageSkeleton />
        ) : (
          <div className="grid gap-6 md:grid-cols-2">
            {CONTEXTS.map((context) => (
              <div key={context}>{renderContextBlock(context)}</div>
            ))}
          </div>
        )}
      </CardContent>

      {pickerContext && (
        <DialogSelectTeamPrompt
          title={t("team.components.pinnedPromptsDefault.addButton")}
          excludeIds={defaults[pickerContext]}
          onSelect={(prompt) => handleAdd(pickerContext, prompt)}
          onClose={() => setPickerContext(null)}
        />
      )}

      <AlertDialog
        open={applyContext !== null}
        onOpenChange={(open) => !open && setApplyContext(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t(
                `team.components.pinnedPromptsDefault.applyConfirm.${confirmContext}.title`,
              )}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                `team.components.pinnedPromptsDefault.applyConfirm.${confirmContext}.description`,
                { count: teamMemberCount },
              )}
              {/* Inside the description so a screen reader announces it with the rest on open. */}
              <span className="mt-2 block font-medium text-destructive">
                {t(
                  "team.components.pinnedPromptsDefault.applyConfirm.personalPinsWarning",
                )}
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isApplying}>
              {t("team.components.pinnedPromptsDefault.applyConfirm.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={isApplying}
              onClick={() => applyContext && handleApply(applyContext)}
            >
              {t("team.components.pinnedPromptsDefault.applyConfirm.confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
